import React, { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { format, formatDistanceToNow } from 'date-fns'
import { Activity, Loader2, ShieldAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { apiTokenService } from '@/services/apiTokenService'

const STATUS_VARIANT = { active: 'success', expired: 'secondary', revoked: 'destructive' }
const AUDIT_VARIANT = { ok: 'outline', error: 'destructive', denied: 'destructive', rate_limited: 'warning' }

// What one token did: its last 100 MCP tool calls / REST requests
const AuditDialog = ({ token, onClose }) => {
  const { data, isLoading } = useQuery(
    ['api-token-audit', token?.id],
    () => apiTokenService.adminAudit(token.id),
    { enabled: !!token }
  )
  const entries = data?.data?.entries || []

  return (
    <Dialog open={!!token} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Activity of "{token?.name}"</DialogTitle>
          <DialogDescription>
            {token?.owner?.name} · last 100 requests and tool calls. Arguments are summarised; contents are never stored.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6">No activity yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Result</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map(e => (
                <TableRow key={e.id}>
                  <TableCell className="text-xs whitespace-nowrap">{format(new Date(e.created_at), 'MMM d, HH:mm:ss')}</TableCell>
                  <TableCell className="text-xs">
                    <Badge variant="outline" className="mr-1 text-[10px]">{e.transport}</Badge>
                    <code>{e.action}</code>
                  </TableCell>
                  <TableCell className="text-xs">{e.target || '—'}</TableCell>
                  <TableCell className="text-xs">
                    <Badge variant={AUDIT_VARIANT[e.status] || 'outline'} className="text-[10px]">{e.status}</Badge>
                    {e.error_code && <span className="ml-1 text-muted-foreground">{e.error_code}</span>}
                  </TableCell>
                  <TableCell className="text-xs">{e.ip || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  )
}

// Admin view of every user's API tokens: who has access, what it can do, revoke if needed
const AdminTokensPanel = () => {
  const queryClient = useQueryClient()
  const [auditToken, setAuditToken] = useState(null)
  const [toRevoke, setToRevoke] = useState(null)
  const { data, isLoading } = useQuery('admin-api-tokens', apiTokenService.adminList)
  const tokens = data?.data?.tokens || []

  const handleRevoke = async () => {
    try {
      await apiTokenService.adminRevoke(toRevoke.id)
      queryClient.invalidateQueries('admin-api-tokens')
      queryClient.invalidateQueries('api-tokens')
      toast.success(`Revoked "${toRevoke.name}" of ${toRevoke.owner?.name}`)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to revoke token')
    } finally {
      setToRevoke(null)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2"><ShieldAlert className="h-5 w-5" /> All tokens and connected apps (admin)</CardTitle>
        <CardDescription>Every user's API tokens and the apps they connected by signing in. Revoke anything that shouldn't exist; review what it did under Activity.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : tokens.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nobody has created a token or connected an app yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Owner</TableHead>
                  <TableHead>Token</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {tokens.map(t => (
                  <TableRow key={t.id} className={t.status !== 'active' ? 'opacity-60' : undefined}>
                    <TableCell>
                      <div className="font-medium text-sm">{t.owner?.name}</div>
                      <div className="text-xs text-muted-foreground">{t.owner?.email} · {t.owner?.role}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{t.name}</div>
                      {t.kind === 'oauth'
                        ? <span className="text-xs text-muted-foreground">Connected app{t.app?.host ? ` · ${t.app.host}` : ''}</span>
                        : <code className="text-xs text-muted-foreground">{t.token_prefix}…</code>}
                    </TableCell>
                    <TableCell className="text-xs max-w-[220px]">{t.scopes.join(', ')}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {t.last_used_at ? formatDistanceToNow(new Date(t.last_used_at), { addSuffix: true }) : 'never'}
                    </TableCell>
                    <TableCell><Badge variant={STATUS_VARIANT[t.status]}>{t.status}</Badge></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => setAuditToken(t)}>
                        <Activity className="mr-1 h-3.5 w-3.5" /> Activity
                      </Button>
                      {t.status === 'active' && (
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setToRevoke(t)}>
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <AuditDialog token={auditToken} onClose={() => setAuditToken(null)} />

      <AlertDialog open={!!toRevoke} onOpenChange={(open) => !open && setToRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke "{toRevoke?.name}" of {toRevoke?.owner?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Every AI tool or script using this token stops working immediately. The owner can create a new one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevoke} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Revoke token
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}

export default AdminTokensPanel
